import {useEffect, useMemo, useState} from 'react';
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import Icon from '@mui/material/Icon';
import axios from 'axios';
import moment from 'moment';
import {Confirm, Notify} from 'notiflix';
import {useSelector} from 'react-redux';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import MDBadge from 'components/MDBadge';
import DataTable from 'examples/Tables/DataTable';
import {ENDPOINT} from 'constants';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';
import {EMAIL_PATTERN, PASSWORD_PATTERN, getRoleBadgeColor} from './moduleConfig';

const EMPTY_FORM = {
  name: '',
  email: '',
  password: '',
  roleId: '',
  isActive: true,
  permissionsOverride: {}, // { [moduleKey]: actionKey[] } — this account's own action grants
  fullAccess: false, // permissionsOverride: ['*'] — full access to every module the role allows
};

// Every action key that appears across the module catalog, in a fixed
// display order — a module's own `actions` array (from GET /roles/modules)
// decides which of these columns actually render for that module's row.
const ACTION_COLUMNS = [
  {key: 'create', label: 'Create'},
  {key: 'read', label: 'Read'},
  {key: 'update', label: 'Update'},
  {key: 'delete', label: 'Delete'},
  {key: 'reveal', label: 'Reveal'},
  {key: 'lock', label: 'Lock'},
  {key: 'purge', label: 'Purge'},
];

const columns = [
  {Header: 'Name', accessor: 'name', align: 'left'},
  {Header: 'Email', accessor: 'email', align: 'left'},
  {Header: 'Role', accessor: 'role', align: 'center'},
  {Header: 'Status', accessor: 'status', align: 'center'},
  {Header: 'Created', accessor: 'createdAt', align: 'center'},
  {Header: 'Actions', accessor: 'actions', align: 'center'},
];

// Settings > Account Management > Accounts. Open to admin and superAdmin:
// create/edit/delete staff accounts and assign one of the dynamic roles
// (fetched from GET /api/v1/roles), which decides what the staff member's
// sidebar and routes show. Only a superAdmin-level account may grant/keep a
// superAdmin-level role itself (enforced below and by the backend).
const AccountsTab = () => {
  const {user} = useSelector((state) => state.auth);
  const canRead = usePermission('account-management', 'read');
  const authHeaders = {headers: {Authorization: `Bearer ${user?.accessToken}`}};
  const api = `${ENDPOINT}api/v1/admin`;
  const rolesApi = `${ENDPOINT}api/v1/roles`;
  const isSuperAdmin = user?.roleDetails?.systemLevel === 'superAdmin';

  const [accounts, setAccounts] = useState([]);
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]); // [{key, label, actions}] — full module/action catalog
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // account being edited, null = new
  const [form, setForm] = useState(EMPTY_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuAccount, setMenuAccount] = useState(null);

  // A non-superAdmin may never edit an admin- or superAdmin-level account
  // (including another admin's) — only their own level and below.
  const canEditAccount = (account) =>
    isSuperAdmin || !['admin', 'superAdmin'].includes(account?.roleDetails?.systemLevel);

  const openRowMenu = (event, account) => {
    setMenuAnchor(event.currentTarget);
    setMenuAccount(account);
  };
  // Only clear menuAnchor here — the Menu keeps rendering through its close
  // transition, and clearing menuAccount immediately made the self-row check
  // below briefly see `null` (so Deactivate/Delete flashed on your own row)
  // during that fade-out. menuAccount is cleared once the transition
  // actually finishes, via the Menu's onExited.
  const closeRowMenu = () => setMenuAnchor(null);

  // Only a superAdmin-level account may grant/keep a superAdmin-level role —
  // hide those roles from the dropdown entirely for anyone else, even though
  // the backend also rejects assigning one.
  const availableRoles = useMemo(
    () => roles.filter((r) => r.systemLevel !== 'superAdmin' || isSuperAdmin),
    [roles, isSuperAdmin],
  );

  const fetchAll = async () => {
    if (!canRead) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [accountsRes, rolesRes, modulesRes] = await Promise.all([
        axios.get(`${api}/all`, authHeaders),
        axios.get(rolesApi, authHeaders),
        axios.get(`${rolesApi}/modules`, authHeaders),
      ]);
      setAccounts(accountsRes.data?.data || []);
      setRoles(rolesRes.data?.data || []);
      setModules(modulesRes.data?.data || []);
    } catch (error) {
      console.error(error);
      Notify.failure(error?.response?.data?.message || 'Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead]);

  const setField = (patch) => setForm((prev) => ({...prev, ...patch}));

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowPassword(false);
    setDialogOpen(true);
  };

  const openEdit = (account) => {
    const isFullAccess =
      Array.isArray(account.permissionsOverride) && account.permissionsOverride.includes('*');
    setEditing(account);
    setForm({
      name: account.name,
      email: account.email,
      password: '',
      roleId: account.roleId || account.roleDetails?.id || '',
      isActive: account.isActive !== false,
      permissionsOverride: isFullAccess ? {} : account.permissionsOverride || {},
      fullAccess: isFullAccess,
    });
    setShowPassword(false);
    setDialogOpen(true);
  };

  const isEditingSelf = Boolean(editing) && editing.id === user?.id;

  // The selected role's own module list decides which rows the action
  // matrix below can even offer — an account can never be granted an action
  // on a module its role doesn't expose at all (the backend rejects this
  // too, see validatePermissionsOverride).
  const selectedRole = roles.find((r) => r.id === form.roleId);
  const roleIsFullAccess = Array.isArray(selectedRole?.permissions) && selectedRole.permissions.includes('*');
  const modulesForSelectedRole = roleIsFullAccess
    ? modules
    : modules.filter((m) => (selectedRole?.permissions || []).includes(m.key));

  const toggleOverrideAction = (moduleKey, action) => {
    setForm((prev) => {
      const current = prev.permissionsOverride[moduleKey] || [];
      const next = current.includes(action)
        ? current.filter((a) => a !== action)
        : [...current, action];

      const permissionsOverride = {...prev.permissionsOverride};
      if (next.length) {
        permissionsOverride[moduleKey] = next;
      } else {
        delete permissionsOverride[moduleKey];
      }
      return {...prev, permissionsOverride};
    });
  };

  const isOverrideChecked = (moduleKey, action) =>
    (form.permissionsOverride[moduleKey] || []).includes(action);

  const validate = () => {
    if (!form.name.trim()) return 'Name is required';
    if (form.name.trim().length < 3) return 'Name must be at least 3 characters';
    if (!form.email.trim()) return 'Email is required';
    if (!EMAIL_PATTERN.test(form.email.trim())) return 'Enter a valid email address';
    if (!form.roleId) return 'Role is required';
    if (!editing && !form.password) return 'Password is required';
    if (form.password && !PASSWORD_PATTERN.test(form.password)) {
      return 'Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number and a special character';
    }
    if (selectedRole?.systemLevel === 'superAdmin' && !isSuperAdmin) {
      return 'Only a superAdmin can assign a superAdmin-level role';
    }
    // System-level roles (SuperAdmin/Admin) and any custom role with Full
    // Access (*) always mean full access — no action matrix is shown for
    // them, so skip this check in that case.
    if (
      !selectedRole?.systemLevel &&
      !roleIsFullAccess &&
      !form.fullAccess &&
      Object.keys(form.permissionsOverride).length === 0
    ) {
      return "Select at least one action for this account, or turn on Full Access";
    }
    return null;
  };

  const handleSave = async () => {
    const error = validate();
    if (error) {
      Notify.warning(error);
      return;
    }
    const permissionsOverridePayload =
      selectedRole?.systemLevel || roleIsFullAccess || form.fullAccess
        ? ['*']
        : form.permissionsOverride;

    try {
      setSaving(true);
      if (editing) {
        // Editing your own account: name, email and password may still
        // change, but roleId/isActive/permissionsOverride are
        // left out of the payload even though the fields are disabled,
        // since the backend also rejects self-changes to those fields with
        // a 403.
        const payload = {name: form.name, email: form.email};
        if (!isEditingSelf) {
          payload.roleId = form.roleId;
          payload.isActive = form.isActive;
          payload.permissionsOverride = permissionsOverridePayload;
        }
        if (form.password) payload.password = form.password;
        await axios.patch(`${api}/update/${editing.id}`, payload, authHeaders);
        Notify.success('Account updated');
      } else {
        await axios.post(
          `${api}/add`,
          {
            name: form.name,
            email: form.email,
            password: form.password,
            roleId: form.roleId,
            permissionsOverride: permissionsOverridePayload,
          },
          authHeaders,
        );
        Notify.success('Account created');
        Notify.info(
          `Password was not included in ${form.name}'s welcome email — share it with them through a secure channel.`,
          {timeout: 8000},
        );
      }
      setDialogOpen(false);
      fetchAll();
    } catch (error) {
      console.error(error);
      Notify.failure(error?.response?.data?.message || 'Failed to save account');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (account) => {
    Confirm.show(
      'Delete account',
      `Delete "${account.name}"'s account? They will no longer be able to sign in.`,
      'Delete',
      'Cancel',
      async () => {
        try {
          await axios.delete(
            `${api}/delete/${account.id}?label=${encodeURIComponent(account.name)}`,
            authHeaders,
          );
          Notify.success('Account deleted');
          fetchAll();
        } catch (error) {
          Notify.failure(error?.response?.data?.message || 'Failed to delete account');
        }
      },
    );
  };

  const handleToggleActive = async (account) => {
    const nextActive = !account.isActive;
    try {
      await axios.patch(`${api}/update/${account.id}`, {isActive: nextActive}, authHeaders);
      Notify.success(`Account ${nextActive ? 'activated' : 'deactivated'}`);
      fetchAll();
    } catch (error) {
      Notify.failure(error?.response?.data?.message || 'Failed to update account status');
    }
  };

  const rows = accounts.map((account) => {
    const role = account.roleDetails;

    return {
      name: (
        <MDTypography variant="button" fontWeight="medium">
          {account.name}
        </MDTypography>
      ),
      email: (
        <MDTypography variant="caption" color="text">
          {account.email}
        </MDTypography>
      ),
      role: role ? (
        <MDBadge
          badgeContent={role.name}
          color={getRoleBadgeColor(role)}
          variant="gradient"
          size="sm"
        />
      ) : (
        <MDTypography variant="caption" color="text">
          —
        </MDTypography>
      ),
      status: (
        <MDBadge
          badgeContent={account.isActive !== false ? 'active' : 'inactive'}
          color={account.isActive !== false ? 'success' : 'secondary'}
          variant="gradient"
          size="sm"
        />
      ),
      createdAt: (
        <MDTypography variant="caption" color="text">
          {account.createdAt ? moment(account.createdAt).format('DD MMM YYYY') : '—'}
        </MDTypography>
      ),
      actions: (
        <MDBox display="flex" justifyContent="center">
          {account.id !== user?.id && !canEditAccount(account) && !isSuperAdmin ? (
            <MDTypography variant="caption" color="text">
              —
            </MDTypography>
          ) : (
            <IconButton size="small" onClick={(e) => openRowMenu(e, account)}>
              <Icon fontSize="small">more_vert</Icon>
            </IconButton>
          )}
        </MDBox>
      ),
    };
  });

  if (!canRead) {
    return (
      <MDBox>
        <NoPermissionNotice message="Your role doesn't include read access to Account Management. Ask an administrator to update your permissions." />
      </MDBox>
    );
  }

  return (
    <>
      <MDBox
        p={3}
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        flexWrap="wrap"
        gap={1}>
        <MDButton variant="gradient" color="info" onClick={openCreate}>
          <Icon sx={{mr: 0.5}}>add</Icon> Add New Account
        </MDButton>
      </MDBox>

      {loading ? (
        <MDBox py={5} textAlign="center">
          <MDTypography variant="button" color="text">
            Loading accounts...
          </MDTypography>
        </MDBox>
      ) : (
        <DataTable
          table={{columns, rows}}
          isSorted={false}
          entriesPerPage={{defaultValue: 10, entries: [10, 25, 50]}}
          showTotalEntries
          noEndBorder
          canSearch
        />
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        fullWidth
        maxWidth="md">
        <DialogTitle>{editing ? 'Edit account' : 'Add new account'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={form.name}
                onChange={(e) => setField({name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={form.email}
                onChange={(e) => setField({email: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={editing ? 'New password (optional)' : 'Password'}
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                helperText="Min 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special character"
                onChange={(e) => setField({password: e.target.value})}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                        <Icon fontSize="small">{showPassword ? 'visibility_off' : 'visibility'}</Icon>
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Tooltip
                title={isEditingSelf ? 'You cannot change your own role or status.' : ''}
                disableHoverListener={!isEditingSelf}>
                <TextField
                  select
                  fullWidth
                  label="Role"
                  value={form.roleId}
                  disabled={isEditingSelf}
                  onChange={(e) => {
                    // Switching roles resets the action grants below — the
                    // old role's modules may not even exist on the new one.
                    setField({roleId: e.target.value, permissionsOverride: {}, fullAccess: false});
                  }}
                  helperText={(() => {
                    const selected = availableRoles.find((r) => r.id === form.roleId);
                    if (!selected) return 'Decides which modules this account can see';
                    if (Array.isArray(selected.permissions) && selected.permissions.includes('*')) {
                      return 'Full access to every module';
                    }
                    const moduleKeys = selected.permissions || [];
                    return moduleKeys.length ? `Modules: ${moduleKeys.join(', ')}` : 'No modules';
                  })()}>
                  {availableRoles.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Tooltip>
            </Grid>
            {form.roleId && !selectedRole?.systemLevel && roleIsFullAccess && (
              <Grid item xs={12}>
                <MDTypography variant="caption" color="text" display="block">
                  This role is assigned full access — every module and action is already
                  granted, so there is nothing to select for this account.
                </MDTypography>
              </Grid>
            )}
            {form.roleId && !selectedRole?.systemLevel && !roleIsFullAccess && (
              <Grid item xs={12}>
                <Tooltip
                  title={isEditingSelf ? 'You cannot change your own permissions.' : ''}
                  disableHoverListener={!isEditingSelf}>
                  <MDBox>
                    <MDTypography variant="button" fontWeight="medium" display="block" mb={0.5}>
                      Permissions for this account
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block" mb={1}>
                      The role above decides which modules this account can see. Pick exactly
                      which actions THIS account gets within those modules — another account on
                      the same role can be given different actions.
                    </MDTypography>
                    <FormControlLabel
                      disabled={isEditingSelf}
                      control={
                        <Checkbox
                          checked={form.fullAccess}
                          onChange={(e) =>
                            setField({fullAccess: e.target.checked, permissionsOverride: {}})
                          }
                        />
                      }
                      label="Full access to every module this role allows"
                    />
                    {!form.fullAccess && (
                      <TableContainer
                        sx={{
                          mt: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          overflowX: 'auto',
                        }}>
                        <Table
                          size="small"
                          sx={{
                            tableLayout: 'fixed',
                            // Keep the table's physical width equal to the sum of its columns.
                            // The container becomes scrollable when that does not fit the dialog.
                            width: 740,
                            minWidth: 740,
                          }}>
                          {/* The dashboard theme makes all table heads `display: block`, which
                              disconnects header column sizing from the body. This matrix needs
                              the browser's native table column model, so reset it locally. */}
                          <TableHead sx={{display: 'table-header-group', p: 0, borderRadius: 0}}>
                            <TableRow>
                              <TableCell sx={{width: 236, minWidth: 236}}>Module</TableCell>
                              {ACTION_COLUMNS.map((col) => (
                                <TableCell
                                  key={col.key}
                                  align="center"
                                  sx={{width: 72, minWidth: 72, px: 0, whiteSpace: 'nowrap'}}>
                                  {col.label}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {modulesForSelectedRole.map((moduleInfo) => (
                              <TableRow key={moduleInfo.key}>
                                <TableCell sx={{width: 236, minWidth: 236}}>
                                  <MDTypography variant="button" fontWeight="regular">
                                    {moduleInfo.label}
                                  </MDTypography>
                                </TableCell>
                                {ACTION_COLUMNS.map((col) =>
                                  moduleInfo.actions.includes(col.key) ? (
                                    <TableCell key={col.key} align="center" sx={{width: 72, minWidth: 72, px: 0}}>
                                      <Checkbox
                                        size="small"
                                        disabled={isEditingSelf}
                                        checked={isOverrideChecked(moduleInfo.key, col.key)}
                                        onChange={() => toggleOverrideAction(moduleInfo.key, col.key)}
                                      />
                                    </TableCell>
                                  ) : (
                                    <TableCell key={col.key} align="center" sx={{width: 72, minWidth: 72, px: 0}}>
                                      —
                                    </TableCell>
                                  ),
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </MDBox>
                </Tooltip>
              </Grid>
            )}
            {editing && (
              <Grid item xs={12}>
                <Tooltip
                  title={isEditingSelf ? 'You cannot change your own role or status.' : ''}
                  disableHoverListener={!isEditingSelf}>
                  <MDBox>
                    <FormControlLabel
                      disabled={isEditingSelf}
                      control={
                        <Switch
                          checked={form.isActive}
                          onChange={(e) => setField({isActive: e.target.checked})}
                        />
                      }
                      label={form.isActive ? 'Active' : 'Inactive'}
                    />
                  </MDBox>
                </Tooltip>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="secondary"
            disabled={saving}
            onClick={() => setDialogOpen(false)}>
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="info" disabled={saving} onClick={handleSave}>
            {saving ? 'Saving...' : editing ? 'Save changes' : 'Create account'}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeRowMenu}
        TransitionProps={{onExited: () => setMenuAccount(null)}}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}>
        {(menuAccount?.id === user?.id || canEditAccount(menuAccount)) && (
          <MenuItem
            onClick={() => {
              openEdit(menuAccount);
              closeRowMenu();
            }}>
            Edit
          </MenuItem>
        )}
        {menuAccount?.id !== user?.id && canEditAccount(menuAccount) && (
          <MenuItem
            onClick={() => {
              handleToggleActive(menuAccount);
              closeRowMenu();
            }}>
            {menuAccount?.isActive !== false ? 'Deactivate' : 'Activate'}
          </MenuItem>
        )}
        {menuAccount?.id !== user?.id && isSuperAdmin && (
          <MenuItem
            onClick={() => {
              handleDelete(menuAccount);
              closeRowMenu();
            }}
            sx={{color: 'error.main'}}>
            Delete
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default AccountsTab;
